import { ISharingProvider, ISharingProviderParams } from './ISharingProvider';
import { ISPListItem } from '../../SPListItemProcessor';
import Promise from '@ms/odsp-utilities/lib/async/Promise';
import { ISharingContext } from '../../interfaces/sharing/ISharingContext';
import {
    IShareResult, ISharingInformation, IShareLinkContext,
    IUnshareLinkContext, IShareLinkResult, SharingRoleV3,
    IUpdatePermissionsResponse, ClientId, IGetSharingInformationResponse
} from '../../interfaces/sharing/SharingInterfaces';
import { IPerson } from '../../dataSources/peoplePicker/IPerson';
import ISharingDataSource, { ISharingDataSourceParams } from '../../dataSources/sharing/ISharingDataSource';
import { SharingDataSource } from '../../dataSources/sharing/SharingDataSource';
import { IApiUrl } from '../../utilities/url/ApiUrlHelper';

/**
 * SharingProvider to support V3 sharing in the list web part.
 */
export class SharingProvider implements ISharingProvider<ISPListItem> {
    private readonly _dataSource: ISharingDataSource;
    private _itemProvider: {
        invalidateItem: (itemId: string, options?: { triggerFetch?: boolean; emptyAllOtherSets?: boolean; refreshSchema?: boolean; }, contextKey?: string) => void;
    };

    public constructor(params: ISharingProviderParams) {
        this._dataSource = params.sharingDataSource || new SharingDataSource(
            {
                getParentListId: params.getParentListId,
                pageContext: params.pageContext
            } as ISharingDataSourceParams
        );
        this._itemProvider = params.itemProvider;
    }

    public getSharingInformation(item: ISPListItem, expandPermissionsInformation?: boolean, sharingLinkTemplates?: boolean, baseUrl?: IApiUrl): Promise<ISharingInformation> {
        return this._dataSource.getSharingInformation(item, expandPermissionsInformation, sharingLinkTemplates, baseUrl);
    }

    public shareLink(context: IShareLinkContext<ISPListItem>): Promise<IShareLinkResult> {
        return this._dataSource.shareLink(context).then((sharingLink: IShareLinkResult) => {
            this._invalidateItem(context.items[0]);
            return sharingLink;
        });
    }

    public unshareLink(context: IUnshareLinkContext<ISPListItem>): Promise<boolean> {
        return this._dataSource.unshareLink(context).then((success: boolean) => {
            this._invalidateItem(context.item);
            return success;
        });
    }

    public updatePermissions2(person: IPerson, item: ISPListItem, role: SharingRoleV3): Promise<IUpdatePermissionsResponse> {
        return this._dataSource.updatePermissions2(person, item, role).then((response: IUpdatePermissionsResponse) => {
            this._invalidateItem(item);
            return response;
        });
    }

    public updatePermissionsMultiple(people: Array<IPerson>, item: ISPListItem, role: SharingRoleV3, notify: boolean, message?: string): Promise<IUpdatePermissionsResponse> {
        return this._dataSource.updatePermissionsMultiple(people, item, role, notify, message).then((response: IUpdatePermissionsResponse) => {
            this._invalidateItem(item);
            return response;
        });
    }

    public getClientId(): ClientId {
        return this._dataSource.getClientId();
    }

    public parseRawSharingInformation(rawSharingInformation: IGetSharingInformationResponse): ISharingInformation {
        return this._dataSource.parseRawSharingInformation(rawSharingInformation);
    }

    public emailWithoutPermissioning?(recipients: Array<IPerson>, item: ISPListItem, message?: string, subject?: string): Promise<IShareResult> {
        return this._dataSource.emailWithoutPermissioning(recipients, item, message, subject).then((result: IShareResult) => {
            return result;
        });
    }

    public checkPermissions(recipients: any, item: ISPListItem) {
        return this._dataSource.checkPermissions(recipients, item);
    }

    public share(people: Array<IPerson>, context: ISharingContext<ISPListItem>, viaV3?: boolean): Promise<IShareResult> {
        return this._dataSource.share(people, context).then((result: IShareResult) => {
            return result;
        });
    }

    private _invalidateItem(item: ISPListItem) {
        if (this._itemProvider && item && item.parent && item.parent.key) {
            this._itemProvider.invalidateItem(item.parent.key);
        }
    }
}
